import React, { createContext, useState, useContext, ReactNode } from 'react'
import Toast from '../components/Toast/Toast'

type ToastType = 'success' | 'error'

interface ToastContextType {
  showSuccess: (message: string, duration?: number) => void
  showError: (message: string, duration?: number) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toast, setToast] = useState<{
    message: string
    type: ToastType
    visible: boolean
  } | null>(null)

  const showToast = (message: string, type: ToastType, duration = 5000) => {
    setToast({ message, type, visible: true })

    const timer = setTimeout(() => {
      setToast(null)
    }, duration)

    return () => clearTimeout(timer)
  }

  const showSuccess = (message: string, duration?: number) =>
    showToast(message, 'success', duration)

  const showError = (message: string, duration?: number) => showToast(message, 'error', duration)

  return (
    <ToastContext.Provider value={{ showSuccess, showError }}>
      {children}
      {toast && <Toast message={toast.message} type={toast.type} visible={toast.visible} />}
    </ToastContext.Provider>
  )
}

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
